import { useRef } from "react";
import Img from "./img";

/** Room kept between the bubble and the screen's edge. */
export const TOOLTIP_EDGE_PX = 8;

/**
 * How far to slide a bubble centered on its icon so it stays on screen.
 *
 * Centered on the icon, a bubble beside the left edge of a phone ran off it:
 * "Stripe Credit Card Fee (2.9% + $0.30)" lost its first words (owner,
 * 2026-11-03). Positive moves it right, negative left, 0 leaves it centered.
 */
export function tooltipShift(left: number, right: number, viewportWidth: number, edge = TOOLTIP_EDGE_PX): number {
	if (left < edge) return edge - left;
	if (right > viewportWidth - edge) return viewportWidth - edge - right;
	return 0;
}

/**
 * The (i) icon with a hover bubble. Styled by `.fee-info-tooltip` in post.scss.
 * `wrap` lets a sentence-long bubble wrap instead of running off the panel.
 *
 * The bubble shows on :hover (a tap, on a phone). As it shows, it is measured
 * and slid back inside the screen; the arrow slides the other way, so it
 * still points at the icon.
 */
const InfoTooltip = ({ text, wrap = false, testId }: { text: string; wrap?: boolean; testId?: string }) => {
	const bubble = useRef<HTMLSpanElement>(null);
	const fit = () => {
		const el = bubble.current;
		if (!el || typeof window === "undefined") return;
		el.style.setProperty("--tooltip-shift", "0px");
		const r = el.getBoundingClientRect();
		// Not drawn (no hover yet): nothing to fit.
		if (r.width === 0) return;
		el.style.setProperty("--tooltip-shift", `${tooltipShift(r.left, r.right, window.innerWidth)}px`);
	};
	return (
		<span
			className={`fee-info-tooltip${wrap ? " fee-info-tooltip--wrap" : ""}`}
			data-testid={testId}
			onPointerEnter={fit}
			onFocus={fit}
			onTouchStart={fit}
		>
			<span className="fee-info-tooltip__icon">
				<Img className="dark-icon" src="/images/svg/info-icon.svg" />
				<Img className="light-icon" src="/images/svg/info-icon-light.svg" />
			</span>
			<span className="fee-info-tooltip__bubble" ref={bubble}>{text}</span>
		</span>
	);
};

export default InfoTooltip;
